import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/student-book-report/StudentBookReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../../_helpers/Helpers";
import {
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { useStandardReportUIContext } from "./StandardReportUIContext";
import { StandardReportFilter } from "./standard-report-filter/StandardReportFilter";
import { StandardReportTable } from "./standard-report-table/StandardReportTable";

export function StandardReportCard() {
  // Standard Report UI Context
  const standardReportUIContext = useStandardReportUIContext();
  const standardReportUIProps = useMemo(() => {
    return {
      queryParams: standardReportUIContext.queryParams
    };
  }, [standardReportUIContext]);
    
  // Getting curret state of Standard Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentBookReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Standard Report Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Standart Kitap Raporunu Excele Aktarır */
  const exportStandardReportToExcel = () => {
    dispatch(actions.getAllStandardReport(standardReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 25, 25, 30, 25, 20, 20];
      const columns = ["Öğretmen", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Adı", "Sayfa Sayısı", "Tarih"];
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookName,
        entity.pageNumber,
        entity.date
      ]));
      const filename = standardReportUIProps.queryParams.term + " - " + (standardReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Standart Kitap Raporu";
      const sheetname = "Öğrenci Standart Kitap Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Standart Kitap Raporunu PDF e Aktarır */
  const exportStandardReportToPDF = () => {
    dispatch(actions.getAllStandardReport(standardReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Öğretmen",
          rows: entities.map(entity => entity.teacher)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Kitap Adı",
          rows: entities.map(entity => entity.bookName)
        },
        {
          header: "Sayfa Sayısı",
          rows: entities.map(entity => entity.pageNumber)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.date)
        }
      ];
  
      const filename = standardReportUIProps.queryParams.term + " - " + (standardReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Standart Kitap Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Standart Kitap Raporunu Yazdırma Penceresini Açar */
  const printStandardReport = () => {
    dispatch(actions.getAllStandardReport(standardReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = standardReportUIProps.queryParams.term + " - " + (standardReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Standart Kitap Raporu";
      const properties = ["Öğretmen", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Adı", "Sayfa Sayısı", "Tarih"];

      entities.forEach(entity => {
        data.push({
          "Öğretmen": entity.teacher,
          "Şube": entity.section,
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Kitap Adı": entity.bookName,
          "Sayfa Sayısı": entity.pageNumber,
          "Tarih": entity.date
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Öğrenci Standart Kitap Raporunu Panoya Kopyalar */
  const copyStandardReport = () => {
    dispatch(actions.getAllStandardReport(standardReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = standardReportUIProps.queryParams.term + " - " + (standardReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Standart Kitap Raporu";
      const columns = ["Öğretmen", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Adı", "Sayfa Sayısı", "Tarih"];
      entities.forEach(entity => rows.push([
        entity.teacher,
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookName,
        entity.pageNumber,
        entity.date
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Standart Rapor">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportStandardReportToExcel}
              exportToPDF={exportStandardReportToPDF}
              print={printStandardReport}
              copy={copyStandardReport}
            />
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <StandardReportFilter />
        <StandardReportTable />
      </CardBody>
    </Card>
  );
}